import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, PackagePlusIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { Button } from './Button';
import { Select } from './Select';
import { TextArea } from './TextArea';
import { TextInput } from './TextInput';
type RecipeStatus = 'Aktiv' | 'Inaktiv';
type WeightType = 'Festgewicht' | 'Verwogen';
type InstructionTiming = 'before' | 'during' | 'after';
type MaterialSource = {
  id: string;
  number: string;
  name: string;
  description: string;
  defaultWeightType: WeightType;
  fixedWeightKg?: number;
};
type RecipeMaterialConfiguration = {
  materialId: string;
  weightType: WeightType;
  targetWeightKg: string;
  instruction: string;
  instructionTiming: InstructionTiming;
};
type Recipe = {
  id: string;
  number: string;
  name: string;
  materialCount: number;
  status: RecipeStatus;
  materialConfigurations: RecipeMaterialConfiguration[];
};
type RecipeFormValues = {
  number: string;
  name: string;
  status: RecipeStatus | '';
};
type RecipeFormErrors = {
  number?: string;
  name?: string;
  status?: string;
  materials?: string;
  targetWeight?: string;
};
const materialSources: MaterialSource[] = [{
  id: 'material-1042',
  number: 'RM-1042',
  name: 'Zuckerstreusel, bunt',
  description: 'Dekorstreusel für Backwaren',
  defaultWeightType: 'Festgewicht',
  fixedWeightKg: 10
}, {
  id: 'material-1043',
  number: 'RM-1043',
  name: 'Weizenmehl Type 405',
  description: 'Universalmehl für Backwaren',
  defaultWeightType: 'Verwogen'
}, {
  id: 'material-1044',
  number: 'RM-1044',
  name: 'Butter, gesalzen',
  description: 'Hochwertige Butter für Teige',
  defaultWeightType: 'Festgewicht',
  fixedWeightKg: 25
}, {
  id: 'material-1045',
  number: 'RM-1045',
  name: 'Schokoladenstücke, dunkel',
  description: '70 % Kakaoanteil, für Gebäck',
  defaultWeightType: 'Verwogen'
}, {
  id: 'material-1046',
  number: 'RM-1046',
  name: 'Vanillezucker',
  description: 'Aromatisierter Zucker',
  defaultWeightType: 'Festgewicht',
  fixedWeightKg: 5
}, {
  id: 'material-1047',
  number: 'RM-1047',
  name: 'Kakaopulver, stark entölt',
  description: 'Feines Kakaopulver für Backwaren',
  defaultWeightType: 'Verwogen'
}];
const recipeStatusOptions = [{
  value: 'Aktiv',
  label: 'Aktiv'
}, {
  value: 'Inaktiv',
  label: 'Inaktiv'
}];
const instructionTimingOptions = [{
  value: 'before',
  label: 'Vor der Wiegung'
}, {
  value: 'during',
  label: 'Während der Wiegung'
}, {
  value: 'after',
  label: 'Nach der Wiegung'
}];
const emptyFormValues: RecipeFormValues = {
  number: '',
  name: '',
  status: ''
};
const initialRecipes: Recipe[] = [{
  id: 'recipe-1042',
  number: 'RZ-1042',
  name: 'Schoko-Crunch',
  materialCount: 6,
  status: 'Aktiv',
  materialConfigurations: createConfigurations(['material-1043', 'material-1045', 'material-1042', 'material-1044', 'material-1046', 'material-1047'], {
    'material-1043': {
      targetWeightKg: '25',
      instruction: 'Langsam zugeben und vollständig einarbeiten.'
    },
    'material-1045': {
      targetWeightKg: '8',
      instruction: 'Erst nach dem Mehl vorsichtig unterheben.'
    }
  })
}, {
  id: 'recipe-1043',
  number: 'RZ-1043',
  name: 'Frucht-Müsli',
  materialCount: 5,
  status: 'Aktiv',
  materialConfigurations: createConfigurations(['material-1042', 'material-1043', 'material-1045', 'material-1046', 'material-1047'])
}, {
  id: 'recipe-1044',
  number: 'RZ-1044',
  name: 'Vanille-Knusper',
  materialCount: 4,
  status: 'Inaktiv',
  materialConfigurations: createConfigurations(['material-1042', 'material-1044', 'material-1046', 'material-1043'])
}, {
  id: 'recipe-1045',
  number: 'RZ-1045',
  name: 'Nuss-Mix Premium',
  materialCount: 3,
  status: 'Inaktiv',
  materialConfigurations: createConfigurations(['material-1043', 'material-1045', 'material-1047'])
}];
export function RecipeManagement() {
  const [recipes, setRecipes] = useState<Recipe[]>(initialRecipes);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [formValues, setFormValues] = useState<RecipeFormValues>(emptyFormValues);
  const [materialConfigurations, setMaterialConfigurations] = useState<RecipeMaterialConfiguration[]>([]);
  const [errors, setErrors] = useState<RecipeFormErrors>({});
  const [isConfiguratorOpen, setIsConfiguratorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RecipeStatus | 'all'>('all');
  const [materialSearchQuery, setMaterialSearchQuery] = useState('');
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase('de-DE');
  const normalizedMaterialSearchQuery = materialSearchQuery.trim().toLocaleLowerCase('de-DE');
  const filteredRecipes = recipes.filter(recipe => {
    const searchableContent = `${recipe.number} ${recipe.name}`.toLocaleLowerCase('de-DE');
    const matchesSearch = !normalizedSearchQuery || searchableContent.includes(normalizedSearchQuery);
    const matchesStatus = statusFilter === 'all' || recipe.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const filteredMaterialSources = materialSources.filter(material => {
    const searchableContent = `${material.number} ${material.name} ${material.description}`.toLocaleLowerCase('de-DE');
    return !normalizedMaterialSearchQuery || searchableContent.includes(normalizedMaterialSearchQuery);
  });
  const selectedMaterial = materialSources.find(material => material.id === selectedMaterialId);
  const selectedConfiguration = materialConfigurations.find(configuration => configuration.materialId === selectedMaterialId);
  const isProductionWeighedMaterial = selectedMaterial?.defaultWeightType === 'Verwogen';
  const hasActiveFilters = Boolean(searchQuery.trim()) || statusFilter !== 'all';
  const isEditing = editingRecipe !== null;
  useEffect(() => {
    const loadingTimeoutId = window.setTimeout(() => setIsLoading(false), 2000);
    return () => window.clearTimeout(loadingTimeoutId);
  }, []);
  useEffect(() => {
    if (!successMessage) {
      return;
    }
    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);
  useEffect(() => {
    if (!isConfiguratorOpen) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeConfigurator();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isConfiguratorOpen]);
  function openCreateConfigurator() {
    setEditingRecipe(null);
    setFormValues(emptyFormValues);
    setMaterialConfigurations([]);
    setMaterialSearchQuery('');
    setSelectedMaterialId(null);
    setErrors({});
    setIsConfiguratorOpen(true);
  }
  function openEditConfigurator(recipe: Recipe) {
    setEditingRecipe(recipe);
    setFormValues({
      number: recipe.number,
      name: recipe.name,
      status: recipe.status
    });
    setMaterialConfigurations(recipe.materialConfigurations);
    setMaterialSearchQuery('');
    setSelectedMaterialId(recipe.materialConfigurations[0]?.materialId ?? null);
    setErrors({});
    setIsConfiguratorOpen(true);
  }
  function closeConfigurator() {
    setIsConfiguratorOpen(false);
    setEditingRecipe(null);
    setFormValues(emptyFormValues);
    setMaterialConfigurations([]);
    setMaterialSearchQuery('');
    setSelectedMaterialId(null);
    setErrors({});
  }
  function resetFilters() {
    setSearchQuery('');
    setStatusFilter('all');
  }
  function updateFormValue<Key extends keyof RecipeFormValues>(key: Key, value: RecipeFormValues[Key]) {
    setFormValues(current => ({
      ...current,
      [key]: value
    }));
    setErrors(current => ({
      ...current,
      [key]: undefined
    }));
  }
  function selectMaterial(material: MaterialSource) {
    setSelectedMaterialId(material.id);
    setErrors(current => ({
      ...current,
      materials: undefined
    }));
    setMaterialConfigurations(currentConfigurations => {
      const isAlreadyConfigured = currentConfigurations.some(configuration => configuration.materialId === material.id);
      if (isAlreadyConfigured) {
        return currentConfigurations;
      }
      return [...currentConfigurations, {
        materialId: material.id,
        weightType: material.defaultWeightType,
        targetWeightKg: material.defaultWeightType === 'Festgewicht' && material.fixedWeightKg !== undefined ? String(material.fixedWeightKg) : '',
        instruction: '',
        instructionTiming: 'during'
      }];
    });
  }
  function updateSelectedConfiguration(updates: Partial<Omit<RecipeMaterialConfiguration, 'materialId' | 'weightType'>>) {
    if (!selectedMaterialId) {
      return;
    }
    setMaterialConfigurations(currentConfigurations => currentConfigurations.map(configuration => configuration.materialId === selectedMaterialId ? {
      ...configuration,
      ...updates
    } : configuration));
    setErrors(current => ({
      ...current,
      targetWeight: undefined
    }));
  }
  function removeSelectedMaterial() {
    if (!selectedMaterialId) {
      return;
    }
    const removedMaterialIndex = materialConfigurations.findIndex(configuration => configuration.materialId === selectedMaterialId);
    const nextSelectedMaterialId = materialConfigurations[removedMaterialIndex + 1]?.materialId ?? materialConfigurations[removedMaterialIndex - 1]?.materialId ?? null;
    setMaterialConfigurations(currentConfigurations => currentConfigurations.filter(configuration => configuration.materialId !== selectedMaterialId));
    setSelectedMaterialId(nextSelectedMaterialId);
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateRecipeForm(formValues, materialConfigurations, recipes, editingRecipe?.id);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !formValues.status) {
      return;
    }
    const normalizedNumber = formValues.number.trim().toUpperCase();
    const normalizedName = formValues.name.trim();
    const recipePayload = {
      number: normalizedNumber,
      name: normalizedName,
      status: formValues.status as RecipeStatus,
      materialCount: materialConfigurations.length,
      materialConfigurations
    };
    if (editingRecipe) {
      setRecipes(currentRecipes => currentRecipes.map(recipe => recipe.id === editingRecipe.id ? {
        ...recipe,
        ...recipePayload
      } : recipe));
      setSuccessMessage(`${normalizedName} wurde aktualisiert.`);
    } else {
      setRecipes(currentRecipes => [...currentRecipes, {
        id: `recipe-${Date.now()}`,
        ...recipePayload
      }]);
      setSuccessMessage(`${normalizedName} wurde angelegt.`);
    }
    setSearchQuery('');
    setStatusFilter('all');
    closeConfigurator();
  }
  return <section className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-696">
      <header className="border-b border-border bg-surface px-5 py-5 sm:px-8 sm:py-6 lg:px-10" data-id="element-697">
        <motion.div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: 'easeOut'
      }} data-id="element-698">
          <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-699">
            Produkte
          </h1>
          {!isConfiguratorOpen ? <Button onClick={openCreateConfigurator} leadingIcon={<PlusIcon size={18} aria-hidden="true" data-id="element-701" />} data-id="element-700">
              Produkt anlegen
            </Button> : null}
        </motion.div>
      </header>

      <div className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 lg:px-10" data-id="element-702">
        <AnimatePresence mode="wait" initial={false} data-id="element-703">
          {isConfiguratorOpen ? <motion.section key="recipe-configurator" aria-label="Produkt konfigurieren" className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.24,
          ease: 'easeOut'
        }} data-id="element-704">
              <form id="recipe-configuration-form" noValidate onSubmit={handleSubmit} className="min-h-0 p-4 sm:p-5" data-id="element-705">
                <div className="border-b border-border pb-5" data-id="element-706">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)_220px]" data-id="element-707">
                    <TextInput id="recipe-number" name="number" label="Produktnummer" placeholder="z. B. RZ-1046" value={formValues.number} onChange={event => updateFormValue('number', event.target.value)} error={errors.number} required data-id="element-708" />
                    <TextInput id="recipe-name" name="name" label="Name" placeholder="Produktbezeichnung" value={formValues.name} onChange={event => updateFormValue('name', event.target.value)} error={errors.name} required data-id="element-709" />
                    <Select id="recipe-status" name="status" label="Status" placeholder="Status auswählen" options={recipeStatusOptions} value={formValues.status} onChange={event => updateFormValue('status', event.target.value as RecipeFormValues['status'])} error={errors.status} required data-id="element-710" />
                  </div>

                  <section className="mt-4" aria-labelledby="selected-materials-title" data-id="element-711">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-712">
                      <h3 id="selected-materials-title" className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-713">
                        Hinzugefügte Rohstoffe
                      </h3>
                      <span className="text-xs text-text-secondary" data-id="element-714">
                        {materialConfigurations.length} ausgewählt
                      </span>
                    </div>

                    {materialConfigurations.length > 0 ? <ul className="mt-2 flex flex-wrap gap-2" aria-label="Bereits hinzugefügte Rohstoffe" data-id="element-715">
                        {materialConfigurations.map(configuration => {
                    const material = materialSources.find(sourceMaterial => sourceMaterial.id === configuration.materialId);
                    if (!material) {
                      return null;
                    }
                    const isActive = selectedMaterialId === configuration.materialId;
                    return <li key={configuration.materialId} data-id="element-716">
                              <button type="button" onClick={() => setSelectedMaterialId(configuration.materialId)} aria-pressed={isActive} className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green ${isActive ? 'border-brand-green bg-brand-yellow-light text-text-primary' : 'border-success/30 bg-success/15 text-success hover:border-success hover:bg-success/20'}`} data-id="element-717">
                                {material.name}
                              </button>
                            </li>;
                  })}
                      </ul> : <p className="mt-2 text-sm text-text-secondary" data-id="element-718">
                        Noch keine Rohstoffe hinzugefügt.
                      </p>}
                  </section>
                </div>

                <div className="mt-5 overflow-hidden rounded-lg border border-border bg-surface" data-id="element-719">
                  <div className="grid min-h-[460px] lg:grid-cols-[minmax(280px,0.8fr)_minmax(0,1.4fr)]" data-id="element-720">
                    <section className="flex min-h-0 flex-col border-b border-border bg-surface lg:border-b-0 lg:border-r" aria-labelledby="material-selection-title" data-id="element-721">
                      <div className="border-b border-border p-4" data-id="element-722">
                        <div className="flex items-center justify-between gap-3" data-id="element-723">
                          <h3 id="material-selection-title" className="font-heading text-base text-text-primary" data-id="element-724">
                            Rohstoffe
                          </h3>
                          <span className="text-xs font-semibold text-text-secondary" data-id="element-725">
                            {materialConfigurations.length} ausgewählt
                          </span>
                        </div>
                        <div className="mt-3" data-id="element-726">
                          <TextInput id="recipe-material-search" name="recipeMaterialSearch" type="search" label="" aria-label="Rohstoffe für das Produkt durchsuchen" placeholder="Nummer, Name oder Beschreibung" value={materialSearchQuery} onChange={event => setMaterialSearchQuery(event.target.value)} data-id="element-727" />
                        </div>
                      </div>

                      <ul className="max-h-[310px] space-y-2 overflow-y-auto p-3 lg:max-h-[440px]" aria-label="Verfügbare Rohstoffe" data-id="element-728">
                        {filteredMaterialSources.length > 0 ? filteredMaterialSources.map(material => {
                      const isSelected = materialConfigurations.some(configuration => configuration.materialId === material.id);
                      const isFocused = selectedMaterialId === material.id;
                      return <li key={material.id} data-id="element-729">
                                <button type="button" onClick={() => selectMaterial(material)} aria-pressed={isSelected} className={`w-full rounded-md border p-3 text-left transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green ${isFocused ? 'border-brand-green bg-brand-yellow-light' : 'border-border bg-surface-subtle hover:border-brand-green hover:bg-surface'} ${isSelected && !isFocused ? 'border-success/50 bg-surface' : ''}`} data-id="element-730">
                                  <span className="flex items-start justify-between gap-3" data-id="element-731">
                                    <span className="min-w-0" data-id="element-732">
                                      <span className="block font-heading text-sm text-text-primary" data-id="element-733">
                                        {material.number}
                                      </span>
                                      <span className="mt-1 block truncate text-sm font-medium text-text-primary" data-id="element-734">
                                        {material.name}
                                      </span>
                                      <span className="mt-0.5 block text-xs text-text-secondary" data-id="element-735">
                                        {material.description}
                                      </span>
                                    </span>
                                    {isSelected ? <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success text-surface" aria-label="Dem Produkt hinzugefügt" data-id="element-736">
                                        <CheckIcon size={13} strokeWidth={3} aria-hidden="true" data-id="element-737" />
                                      </span> : <PackagePlusIcon size={17} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" data-id="element-738" />}
                                  </span>
                                </button>
                              </li>;
                    }) : <li className="px-3 py-8 text-center text-sm text-text-secondary" data-id="element-739">
                            Keine passenden Rohstoffe gefunden.
                          </li>}
                      </ul>
                    </section>

                    <section className="min-h-0 bg-surface p-4 sm:p-5" aria-labelledby="material-detail-title" data-id="element-740">
                      {selectedMaterial && selectedConfiguration ? <motion.div key={selectedMaterial.id} initial={{
                    opacity: 0,
                    x: 8
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} data-id="element-741">
                          <header className="flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-start sm:justify-between" data-id="element-742">
                            <div className="min-w-0" data-id="element-743">
                              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-744">
                                Ausgewählter Rohstoff
                              </p>
                              <h3 id="material-detail-title" className="mt-2 font-heading text-2xl leading-tight text-text-primary" data-id="element-745">
                                {selectedMaterial.name}
                              </h3>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={removeSelectedMaterial} leadingIcon={<XIcon size={16} aria-hidden="true" data-id="element-747" />} data-id="element-746">
                              Entfernen
                            </Button>
                          </header>

                          <section className="mt-6 rounded-lg border border-border bg-surface-subtle p-5 sm:p-6" aria-labelledby="weight-briefing-title" data-id="element-748">
                            <div className="grid gap-6 lg:grid-cols-[minmax(180px,0.75fr)_minmax(0,1fr)] lg:items-end" data-id="element-749">
                              <div data-id="element-750">
                                <p id="weight-briefing-title" className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-751">
                                  {isProductionWeighedMaterial ? 'Sollmenge' : 'Festgewicht aus Rohstoff'}
                                </p>
                                <p className="mt-3 font-heading text-5xl leading-none text-brand-green sm:text-6xl" data-id="element-752">
                                  {isProductionWeighedMaterial ? formatEnteredKilograms(selectedConfiguration.targetWeightKg) : selectedMaterial.fixedWeightKg !== undefined ? formatKilograms(selectedMaterial.fixedWeightKg) : '—'}
                                  {isProductionWeighedMaterial && isPositiveWeight(selectedConfiguration.targetWeightKg) || !isProductionWeighedMaterial && selectedMaterial.fixedWeightKg !== undefined ? <span className="ml-2 text-xl font-normal text-text-secondary sm:text-2xl" data-id="element-753">
                                      kg
                                    </span> : null}
                                </p>
                              </div>

                              {isProductionWeighedMaterial ? <TextInput id="recipe-material-target-weight" name="targetWeight" type="text" inputMode="decimal" label="Sollmenge in kg" placeholder="z. B. 25" value={selectedConfiguration.targetWeightKg} onChange={event => updateSelectedConfiguration({
                          targetWeightKg: event.target.value
                        })} error={errors.targetWeight} required data-id="element-754" /> : null}

                              <p className="text-sm leading-6 text-text-secondary lg:col-span-2" data-id="element-755">
                                {isProductionWeighedMaterial ? 'Dieser Rohstoff wird in der Produktion verwogen. Bitte legen Sie die Sollmenge für dieses Produkt fest.' : 'Das Festgewicht wird automatisch aus den Rohstoffstammdaten übernommen.'}
                              </p>
                            </div>
                          </section>

                          <section className="mt-6" aria-labelledby="instruction-briefing-title" data-id="element-756">
                            <p id="instruction-briefing-title" className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-757">
                              Produktionshinweis
                            </p>
                            <div className="mt-3 rounded-lg border border-border bg-surface p-4 sm:p-5" data-id="element-758">
                              <div className="grid gap-4" data-id="element-759">
                                <Select id="recipe-material-instruction-timing" name="instructionTiming" label="Hinweis anzeigen" options={instructionTimingOptions} value={selectedConfiguration.instructionTiming} onChange={event => updateSelectedConfiguration({
                            instructionTiming: event.target.value as InstructionTiming
                          })} data-id="element-760" />
                                <TextArea id="recipe-material-instruction" name="instruction" label="Produktionshinweis" hideLabel placeholder="Anweisungen für die Produktion hinterlegen …" rows={4} className="resize-y" value={selectedConfiguration.instruction} onChange={event => updateSelectedConfiguration({
                            instruction: event.target.value
                          })} data-id="element-761" />
                              </div>
                            </div>
                          </section>
                        </motion.div> : <div className="flex min-h-[340px] flex-col items-center justify-center px-5 text-center" data-id="element-762">
                          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-yellow-light text-text-primary" aria-hidden="true" data-id="element-763">
                            <PackagePlusIcon size={22} data-id="element-764" />
                          </span>
                          <h3 id="material-detail-title" className="mt-4 font-heading text-lg text-text-primary" data-id="element-765">
                            Rohstoff auswählen
                          </h3>
                          <p className="mt-1 max-w-sm text-sm leading-6 text-text-secondary" data-id="element-766">
                            Wählen Sie links einen Rohstoff aus. Er wird direkt
                            zum Produkt hinzugefügt und kann hier konfiguriert
                            werden.
                          </p>
                        </div>}
                    </section>
                  </div>
                </div>

                {errors.materials ? <p className="mt-3 text-sm font-medium text-error" role="alert" data-id="element-767">
                    {errors.materials}
                  </p> : null}

                <div className="mt-5 flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end" data-id="element-768">
                  <Button type="button" variant="secondary" onClick={closeConfigurator} data-id="element-769">
                    Abbrechen
                  </Button>
                  <Button type="submit" data-id="element-770">
                    {isEditing ? 'Änderungen speichern' : 'Produkt speichern'}
                  </Button>
                </div>
              </form>
            </motion.section> : <motion.section key="recipe-overview" aria-label="Produktübersicht" className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.24,
          ease: 'easeOut'
        }} data-id="element-771">
              <div className="border-b border-border bg-surface px-4 py-4 sm:px-5" data-id="element-772">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end" data-id="element-773">
                  <div className="min-w-0 flex-1" data-id="element-774">
                    <TextInput id="recipe-search" name="recipeSearch" type="search" label="" aria-label="Produkte durchsuchen" placeholder="Produktnummer oder Name" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} data-id="element-775" />
                  </div>
                  <div className="w-full sm:w-[260px]" data-id="element-776">
                    <Select id="recipe-status-filter" name="recipeStatusFilter" label="" aria-label="Produkte nach Status filtern" options={[{
                  value: 'all',
                  label: 'Alle Status'
                }, ...recipeStatusOptions]} value={statusFilter} onChange={event => setStatusFilter(event.target.value as RecipeStatus | 'all')} data-id="element-777" />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto" data-id="element-778">
                <table className="w-full min-w-[840px] table-fixed text-left" data-id="element-779">
                  <caption className="sr-only" data-id="element-780">
                    Übersicht aller Produkte mit Produktnummer, Name, Anzahl der
                    Rohstoffe, Status und Bearbeiten-Aktion.
                  </caption>
                  <colgroup data-id="element-781">
                    <col className="w-[16%]" data-id="element-782" />
                    <col className="w-[31%]" data-id="element-783" />
                    <col className="w-[23%]" data-id="element-784" />
                    <col className="w-[15%]" data-id="element-785" />
                    <col className="w-[15%]" data-id="element-786" />
                  </colgroup>
                  <thead className="border-b border-border bg-surface-subtle" data-id="element-787">
                    <tr className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-788">
                      <th scope="col" className="px-5 py-4" data-id="element-789">
                        Produktnummer
                      </th>
                      <th scope="col" className="px-4 py-4" data-id="element-790">
                        Name
                      </th>
                      <th scope="col" className="px-4 py-4" data-id="element-791">
                        Rohstoffe
                      </th>
                      <th scope="col" className="px-4 py-4" data-id="element-792">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-4" data-id="element-793">
                        Aktion
                      </th>
                    </tr>
                  </thead>
                  <tbody aria-busy={isLoading} aria-label={isLoading ? 'Produkte werden geladen' : 'Produkte'} data-id="element-794">
                    {isLoading ? <RecipeTableSkeleton data-id="element-795" /> : filteredRecipes.length > 0 ? filteredRecipes.map((recipe, index) => <motion.tr key={recipe.id} className="border-b border-border last:border-b-0 hover:bg-surface-subtle" initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.22,
                  delay: index * 0.045,
                  ease: 'easeOut'
                }} data-id="element-796">
                          <th scope="row" className="whitespace-nowrap px-5 py-4 align-middle font-heading text-sm text-text-primary" data-id="element-797">
                            {recipe.number}
                          </th>
                          <td className="px-4 py-4 align-middle font-heading text-sm text-text-primary" data-id="element-798">
                            {recipe.name}
                          </td>
                          <td className="px-4 py-4 align-middle text-sm text-text-secondary" data-id="element-799">
                            {formatMaterialCount(recipe.materialCount)}
                          </td>
                          <td className="px-4 py-4 align-middle" data-id="element-800">
                            <RecipeStatusBadge status={recipe.status} data-id="element-801" />
                          </td>
                          <td className="px-4 py-4 align-middle" data-id="element-802">
                            <Button variant="ghost" size="sm" onClick={() => openEditConfigurator(recipe)} leadingIcon={<PencilIcon size={17} aria-hidden="true" data-id="element-804" />} aria-label={`${recipe.name} bearbeiten`} title={`${recipe.name} bearbeiten`} data-id="element-803">
                              <span className="sr-only" data-id="element-805">Bearbeiten</span>
                            </Button>
                          </td>
                        </motion.tr>) : <tr data-id="element-806">
                        <td colSpan={5} className="px-5 py-12 text-center" data-id="element-807">
                          <p className="font-heading text-base text-text-primary" data-id="element-808">
                            {hasActiveFilters ? 'Keine passenden Produkte gefunden' : 'Noch keine Produkte angelegt'}
                          </p>
                          <p className="mt-1 text-sm text-text-secondary" data-id="element-809">
                            {hasActiveFilters ? 'Passen Sie die Suche oder den Filter an.' : 'Legen Sie ein Produkt an, um die Produktübersicht zu starten.'}
                          </p>
                          {hasActiveFilters ? <Button type="button" variant="ghost" size="sm" className="mt-3" onClick={resetFilters} data-id="element-810">
                              Filter zurücksetzen
                            </Button> : <Button className="mt-4" size="sm" onClick={openCreateConfigurator} leadingIcon={<PlusIcon size={16} aria-hidden="true" data-id="element-812" />} data-id="element-811">
                              Produkt anlegen
                            </Button>}
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </motion.section>}
        </AnimatePresence>

        {!isConfiguratorOpen ? <p className="mt-3 text-xs text-text-secondary sm:hidden" data-id="element-813">
            Für weitere Spalten horizontal scrollen.
          </p> : null}
      </div>

      <AnimatePresence data-id="element-814">
        {successMessage ? <motion.div role="status" aria-live="polite" className="fixed right-5 top-5 z-30 flex max-w-sm items-start gap-3 rounded-lg border border-success bg-surface p-4 shadow-md sm:right-8 sm:top-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} data-id="element-815">
            <CheckCircle2Icon size={20} className="mt-0.5 shrink-0 text-success" aria-hidden="true" data-id="element-816" />
            <div data-id="element-817">
              <p className="font-heading text-sm text-text-primary" data-id="element-818">
                Produktverwaltung aktualisiert
              </p>
              <p className="mt-1 text-xs leading-5 text-text-secondary" data-id="element-819">
                {successMessage}
              </p>
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </section>;
}
type RecipeStatusBadgeProps = {
  status: RecipeStatus;
};
function RecipeStatusBadge({
  status
}: RecipeStatusBadgeProps) {
  const isActive = status === 'Aktiv';
  return <span className={`inline-flex items-center gap-2 text-sm font-medium ${isActive ? 'text-success' : 'text-error'}`} data-id="element-820">
      <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-success' : 'bg-error'}`} aria-hidden="true" data-id="element-821" />
      {status}
    </span>;
}
function RecipeTableSkeleton() {
  return <>
      <tr className="sr-only" data-id="element-822">
        <td colSpan={5} data-id="element-823">Produkte werden geladen.</td>
      </tr>
      {Array.from({
      length: 5
    }, (_, index) => <tr key={`recipe-skeleton-${index}`} aria-hidden="true" className="animate-pulse border-b border-border last:border-b-0" data-id="element-824">
            <td className="px-5 py-4" data-id="element-825">
              <div className="h-4 w-20 rounded bg-border" data-id="element-826" />
            </td>
            <td className="px-4 py-4" data-id="element-827">
              <div className="h-4 w-40 rounded bg-border" data-id="element-828" />
            </td>
            <td className="px-4 py-4" data-id="element-829">
              <div className="h-4 w-24 rounded bg-border" data-id="element-830" />
            </td>
            <td className="px-4 py-4" data-id="element-831">
              <div className="h-7 w-20 rounded-md bg-border" data-id="element-832" />
            </td>
            <td className="px-4 py-4" data-id="element-833">
              <div className="h-8 w-8 rounded-md bg-border" data-id="element-834" />
            </td>
          </tr>)}
    </>;
}
function createConfigurations(materialIds: string[], overrides: Partial<Record<string, Partial<Omit<RecipeMaterialConfiguration, 'materialId' | 'weightType'>>>> = {}): RecipeMaterialConfiguration[] {
  return materialIds.flatMap(materialId => {
    const material = materialSources.find(sourceMaterial => sourceMaterial.id === materialId);
    if (!material) {
      return [];
    }
    return [{
      materialId,
      weightType: material.defaultWeightType,
      targetWeightKg: material.defaultWeightType === 'Festgewicht' && material.fixedWeightKg !== undefined ? String(material.fixedWeightKg) : '',
      instruction: '',
      instructionTiming: 'during',
      ...overrides[materialId]
    }];
  });
}
function validateRecipeForm(values: RecipeFormValues, materialConfigurations: RecipeMaterialConfiguration[], recipes: Recipe[], editingRecipeId?: string): RecipeFormErrors {
  const errors: RecipeFormErrors = {};
  const normalizedNumber = values.number.trim().toUpperCase();
  if (!normalizedNumber) {
    errors.number = 'Bitte geben Sie eine Produktnummer ein.';
  } else if (recipes.some(recipe => recipe.id !== editingRecipeId && recipe.number.toUpperCase() === normalizedNumber)) {
    errors.number = 'Diese Produktnummer ist bereits vergeben.';
  }
  if (!values.name.trim()) {
    errors.name = 'Bitte geben Sie einen Namen ein.';
  }
  if (!values.status) {
    errors.status = 'Bitte wählen Sie einen Status aus.';
  }
  if (materialConfigurations.length === 0) {
    errors.materials = 'Bitte wählen Sie mindestens einen Rohstoff aus.';
  }
  if (materialConfigurations.some(configuration => {
    const material = materialSources.find(sourceMaterial => sourceMaterial.id === configuration.materialId);
    return material?.defaultWeightType === 'Verwogen' && !isPositiveWeight(configuration.targetWeightKg);
  })) {
    errors.targetWeight = 'Bitte geben Sie für jeden verwogenen Rohstoff eine Kilogrammzahl größer als 0 ein.';
  }
  return errors;
}
function isPositiveWeight(value: string) {
  const weight = Number(value.trim().replace(',', '.'));
  return Number.isFinite(weight) && weight > 0;
}
function formatMaterialCount(materialCount: number) {
  return `${materialCount} ${materialCount === 1 ? 'Rohstoff' : 'Rohstoffe'}`;
}
function formatEnteredKilograms(value: string) {
  const weight = Number(value.trim().replace(',', '.'));
  return Number.isFinite(weight) && weight > 0 ? formatKilograms(weight) : '—';
}
function formatKilograms(weightKg: number) {
  return weightKg.toLocaleString('de-DE', {
    maximumFractionDigits: 3
  });
}